import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'

// ============================================================================
// ANCHORED POPOVERS — the placement and the dismissal, in one place because the detail peek and
// the block editor are the same object with different contents, and two copies of this drift.
//
// THE BUG THIS FILE EXISTS TO KILL (owner report, 2026-08-28: the peek "pops up in random places
// sometimes off the screen completely"). It was not random. The app runs at `zoom: 1.3` by
// default (lib/scale.ts), and under CSS `zoom` on <html> the two halves of the old arithmetic
// were in DIFFERENT UNITS:
//
//   · `getBoundingClientRect()` on the clicked block returns VIEWPORT pixels — what is actually
//     on the glass, zoom already applied;
//   · a `position: fixed` panel INSIDE the zoomed root resolves its own `left`/`top` in the
//     zoomed element's local pixels, which the browser then multiplies by 1.3 to paint.
//
// So writing `left: rect.left` painted the panel at `rect.left * 1.3`. A block two thirds of the
// way across the week landed past the right edge of the screen; the clamp could not save it
// because `window.innerWidth` is a viewport number being compared against local ones. Hence the
// bottom-right corner in the owner's screenshot, and hence `rootZoom` below: measure the factor,
// do every comparison in viewport pixels, and divide exactly once on the way out.
//
// The geometry itself is a PURE FUNCTION taking numbers, so the clamp is unit-tested rather than
// left to a screenshot — happy-dom has no layout, but it does not need any to check that a rect
// near the right edge comes back on the left of its anchor and inside the viewport.
// ============================================================================

/** The rectangle a popover is drawn beside. Viewport pixels, captured once at click time. */
export interface PeekAnchor {
  top: number
  left: number
  right: number
  bottom: number
}

/** Breathing room between the anchor and the panel, and between the panel and the screen edge. */
export const POPOVER_GAP = 8

export interface Box {
  width: number
  height: number
}

export interface Placement {
  left: number
  top: number
}

/**
 * Where an anchored panel goes: BESIDE the thing that opened it, on the side with the most room,
 * and never off the screen.
 *
 * THREE PLACEMENTS, TRIED IN ORDER, then a clamp that is unconditional.
 *
 *   1. RIGHT of the anchor, if the panel fits there. Tops aligned — a panel whose top edge lines
 *      up with the block's reads as belonging to that block; one centred on it does not, and one
 *      hung below it (the old behaviour) covers the next hour of the grid, which is the hour you
 *      are most likely to be comparing against.
 *   2. LEFT of it, same rule, for a block in Sunday's column.
 *   3. BELOW it (or above, when there is no room below), aligned to its left edge. This is the
 *      DAY VIEW's normal case and not an edge case: a day-view block is the full width of the
 *      card, so neither side has 252px and forcing one would put the panel on top of the block it
 *      is describing. Under it, the block stays readable and the relationship still reads.
 *
 * The clamp is what makes "it can never render off-screen" a property of the function rather than
 * a hope about the inputs: a viewport narrower than the panel still yields `POPOVER_GAP`, which is
 * a panel with its right edge cut off rather than a panel nobody can find.
 */
export function placeAnchored(
  anchor: PeekAnchor,
  panel: Box,
  viewport: Box,
  gap: number = POPOVER_GAP,
): Placement {
  const fitsRight = viewport.width - anchor.right - gap >= panel.width
  const fitsLeft = anchor.left - gap >= panel.width

  if (fitsRight || fitsLeft) {
    const x = fitsRight ? anchor.right + gap : anchor.left - gap - panel.width
    return {
      left: clamp(x, gap, viewport.width - panel.width - gap),
      top: clamp(anchor.top, gap, viewport.height - panel.height - gap),
    }
  }

  const below = anchor.bottom + gap
  const above = anchor.top - gap - panel.height
  const y = below + panel.height <= viewport.height - gap ? below : above
  return {
    left: clamp(anchor.left, gap, viewport.width - panel.width - gap),
    top: clamp(y, gap, viewport.height - panel.height - gap),
  }
}

/** Clamped into [min, max] — and to `min` when the panel is simply bigger than the room. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

/**
 * The factor CSS `zoom` is applying to the document, measured rather than imported.
 *
 * `offsetWidth` is the root's own layout width in unzoomed pixels; `getBoundingClientRect().width`
 * is the same edge in painted viewport pixels. Their ratio IS the zoom, at any value the scale
 * control (or the browser's own Ctrl+=) happens to be at, with nothing here having to know which.
 * Reading `SCALE_FACTOR` instead would be a second source of truth that goes stale the first time
 * the owner presses Ctrl+= on top of the app's own 130%.
 *
 * Degrades to 1 — the pre-zoom behaviour — whenever the measurement is unavailable (happy-dom
 * reports 0 for both), because a panel placed as if unzoomed is the bug we already had, and
 * dividing by a bogus factor would be a new and stranger one.
 */
export function rootZoom(doc: Document | null | undefined): number {
  const root = doc?.documentElement
  if (!root) return 1
  const layout = root.offsetWidth
  const painted = root.getBoundingClientRect?.().width ?? 0
  if (!layout || !painted) return 1
  const zoom = painted / layout
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1
}

/**
 * Place a panel beside its anchor, in the local pixels its own `style` will be read in.
 *
 * MEASURE, THEN PLACE. The panel's height is not knowable ahead of a real layout pass — a course
 * link adds a row, a status line adds another, an error message adds two — so this runs as a
 * layout effect against the element's actual rendered box and lands before the browser paints the
 * first frame. There is nothing to see re-jump.
 */
export function useAnchoredPlacement(
  ref: RefObject<HTMLElement | null>,
  anchor: PeekAnchor,
  fallbackWidth: number,
): Placement {
  const [placement, setPlacement] = useState<Placement>({
    left: anchor.left,
    top: anchor.top,
  })

  useLayoutEffect(() => {
    const zoom = rootZoom(typeof document === 'undefined' ? null : document)
    const box = ref.current?.getBoundingClientRect()
    const panel = {
      // A first paint with no box yet still needs a width to reason about; the design constant,
      // scaled, is the right guess because it is what the CSS asked for.
      width: box?.width || fallbackWidth * zoom,
      height: box?.height ?? 0,
    }
    const viewport = { width: window.innerWidth, height: window.innerHeight }
    const placed = placeAnchored(anchor, panel, viewport)
    // THE ONE DIVISION. Everything above is viewport pixels; `style.left` is local pixels.
    setPlacement({ left: placed.left / zoom, top: placed.top / zoom })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor.top, anchor.left, anchor.right, anchor.bottom, fallbackWidth])

  return placement
}

/**
 * ONE CLICK CLOSES, AND DOES NOTHING ELSE.
 *
 * Without this, dismissing a popover by clicking the grid behind it closed the popover AND opened
 * a new-event composer at whatever hour the pointer happened to be over — the screen acting on a
 * click the owner spent on "go away". There was no established swallow in this codebase to copy
 * (the old peek listened on `pointerdown` and let the click through), so the call is made here:
 * the first outside click is consumed.
 *
 * It cannot live in the effect below, because closing unmounts the component and the cleanup would
 * remove the listener before the `click` — which arrives on pointerUP, later — was ever dispatched.
 * So it is a free-standing listener that removes itself: on the click it eats, or on a timer when
 * the gesture turns out not to produce one (a drag, a right-click, a pointercancel).
 */
export function swallowNextClick(win: Window): void {
  let timer = 0
  const swallow = (event: Event) => {
    event.stopPropagation()
    event.preventDefault()
    win.clearTimeout(timer)
  }
  win.addEventListener('click', swallow, { capture: true, once: true })
  timer = win.setTimeout(() => win.removeEventListener('click', swallow, true), 500)
}

/**
 * Escape closes; a pointer down outside closes; the click that closed is eaten.
 *
 * LIGHT DISMISS, NOT A SCRIM. There is no full-screen catcher behind these panels — the grid
 * underneath stays hoverable and scrollable, which is the whole point of a peek you open to
 * compare something against the week around it. Armed one tick after mount so the very click that
 * OPENED the panel (still finishing its bubble to `document`) can never be read as the one that
 * closes it again.
 */
export function useDismissable(ref: RefObject<HTMLElement | null>, onClose: () => void): void {
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      close.current()
    }
    function onPointerDown(event: Event) {
      if (ref.current?.contains(event.target as Node)) return
      swallowNextClick(window)
      close.current()
    }
    const armId = window.setTimeout(
      () => document.addEventListener('pointerdown', onPointerDown),
      0,
    )
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(armId)
      window.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [ref])
}

/**
 * The one-frame-late entrance flag every panel on this screen shares: `false` on the first paint,
 * `true` on the next, so a CSS transition has two states to travel between instead of a panel
 * that was simply always there.
 *
 * AND A WATCHDOG BEHIND THE FRAME, which is not belt-and-braces — it is the same lesson
 * lib/motion.ts writes out at length. `requestAnimationFrame` only fires while the document is
 * RENDERING: in a hidden tab, a backgrounded window, or an automation pane that is not
 * compositing (found exactly that way here), it never fires at all — and a panel whose only
 * route from `opacity: 0` to `opacity: 1` is a frame callback would then stay invisible forever.
 * The peek would merely look broken; the COMPOSER would be an invisible form with the cursor
 * already in it. So whichever arrives first wins, and the timeout is the floor.
 *
 * The animation is the garnish; the panel being on screen is the meal, and it is never allowed
 * to depend on a frame being painted.
 */
export function useEntered(): boolean {
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    const timer = window.setTimeout(() => setEntered(true), 50)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [])
  return entered
}

/**
 * A click on empty grid, as an anchor. A caret rather than a box: there is no element behind a
 * click on the background, so the thing the editor is "beside" is the pointer itself.
 */
export function anchorAtPoint(clientX: number, clientY: number): PeekAnchor {
  return { top: clientY, bottom: clientY, left: clientX, right: clientX }
}

/** An element's own rect, narrowed to the four numbers a placement needs. */
export function anchorOfElement(el: Element): PeekAnchor {
  const box = el.getBoundingClientRect()
  return { top: box.top, left: box.left, right: box.right, bottom: box.bottom }
}
